import {
	Button,
	InputNumber,
	Modal,
	Form,
	Checkbox,
	message,
	Typography,
	Select,
	Alert,
	Input,
	List,
} from 'antd';
import { Material } from '../../interfaces/MaterialInventory.interface';
import {
	useAddMaterialLengthsMutation,
	useCreateMaterialRemnantMutation,
	useGetMaterialShelvesQuery,
	useGetMaterialByIdQuery,
} from '../../redux/api/material';
import { useEffect, useState } from 'react';
import {
	ANGLE,
	CHANNEL,
	HEX_BAR,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
} from '../../config/constants';
import { getEffectiveShapeName } from '../../utils/shapeUtils';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const ReturnRemnantsModal = ({
	isReturnRemnantsModalOpen,
	onClose,
	materialId,
	refetchData,
	useTabletMode,
}: {
	isReturnRemnantsModalOpen: boolean;
	onClose: () => void;
	materialId: string;
	refetchData: () => void;
	useTabletMode: boolean;
}) => {
	const [form] = Form.useForm();
	const materialShelvesQuery = useGetMaterialShelvesQuery();
	const [addMaterialLengths, addMaterialLengthsResult] = useAddMaterialLengthsMutation(); // RTK Query mutation hook
	const [createRemnant, createRemnantResult] = useCreateMaterialRemnantMutation(); // RTK Query mutation hook

	const material = useGetMaterialByIdQuery(
		{ id: materialId },
		{ skip: !materialId, refetchOnMountOrArgChange: true }
	);

	const decrement = () => {
		const currentValue = form.getFieldValue('quantity') || 1;
		if (currentValue > 1) {
			form.setFieldsValue({ quantity: currentValue - 1 });
		}
	};

	const increment = () => {
		const currentValue = form.getFieldValue('quantity') || 1;
		if (currentValue < 99) {
			form.setFieldsValue({ quantity: currentValue + 1 });
		}
	};

	const [overrideWidth, setOverrideWidth] = useState(false);
	const [overrideHeight, setOverrideHeight] = useState(false);
	const [overrideOuterDiameter, setOverrideOuterDiameter] = useState(false);
	const [overrideInnerDiameter, setOverrideInnerDiameter] = useState(false);
	const [wallThickness, setWallThickness] = useState(false);

	const [chooseLocationAutomatically, setChooseLocationAutomatically] = useState<boolean>(true);

	const [remnantsAdded, setRemnantsAdded] = useState(false);

	const handleClose = () => {
		onClose();
		setRemnantsAdded(false);
		setChooseLocationAutomatically(true);
		form.resetFields();
		setOverrideWidth(false);
		setOverrideHeight(false);
		setOverrideOuterDiameter(false);
		setOverrideInnerDiameter(false);
		setWallThickness(false);
	};

	// Handler for form submission
	const handleOk = () => {
		form
			.validateFields()
			.then((values) => {
				if (overrideHeight ||
						overrideWidth ||
						overrideOuterDiameter ||
						overrideInnerDiameter ||
						wallThickness) {
					// Add material.id to the request body
					const requestData = {
						// values: { ...values, material: material.id },
						id: material?.data?.id,
						values: {
							current_length: values.current_length,
							quantity: values.quantity,
							width: values.current_width,
							height: values.current_height,
							outer_diameter: values.current_outer_diameter,
							inner_diameter: values.current_inner_diameter,
							wall_thickness: values.current_wall_thickness,
						},
					};
					// Make API call using RTK Query mutation
					createRemnant(requestData)
						.unwrap() // Unwrap the promise for handling success or failure
						.then((data) => {
							message.success('Material length created successfully!');
							form.resetFields();
							refetchData();
							onClose();
						})
						.catch((error) => {
							message.error(error.data);
						});
				} else {
					const requestData = {
						material_lengths: [{ ...values }],
						material: material?.data?.id,
						movement_reason: 'remnant_return',
					};

					addMaterialLengths(requestData)
						.unwrap() // Unwrap the promise for handling success or failure
						.then((data) => {
							message.success('Material length created successfully!');
							form.resetFields();
							refetchData();
						})
						.catch((error) => {
							message.error(error.data);
						});
				}
			})
			.catch((info) => {
				console.error('Validate Failed:', info);
			});
	};

	useEffect(() => {
		if (addMaterialLengthsResult.isSuccess) {
			setRemnantsAdded(true);
			form.resetFields();
		}
	}, [addMaterialLengthsResult]);

	const materialShapeName = getEffectiveShapeName(material?.data?.shape?.display_name);

	return (
		<Modal
			open={isReturnRemnantsModalOpen}
			width="560px"
			maskClosable={false}
			closable={false}
			centered
			onCancel={handleClose}
			footer={[
				<>
					{remnantsAdded ? (
						<>
							<Button
								key="cancel"
								onClick={handleClose}
								size={useTabletMode ? 'large' : 'middle'}
							>
								Done
							</Button>
							<Button
								type="primary"
								loading={
									addMaterialLengthsResult.isLoading ||
									createRemnantResult.isLoading
								}
								onClick={() => setRemnantsAdded(false)}
								size={useTabletMode ? 'large' : 'middle'}
							>
								Add Another
							</Button>
						</>
					) : (
						<>
							<Button
								key="cancel"
								onClick={handleClose}
								size={useTabletMode ? 'large' : 'middle'}
							>
								Cancel
							</Button>
							<Button
								key="submit"
								type="primary"
								loading={
									addMaterialLengthsResult.isLoading ||
									createRemnantResult.isLoading
								}
								onClick={handleOk}
								size={useTabletMode ? 'large' : 'middle'}
							>
								Submit
							</Button>
						</>
					)}
				</>,
			]}
		>
			{remnantsAdded ? (
				<div>
					<Alert
						type="success"
						showIcon={true}
						message={<Title level={5}>Remnants Added!</Title>}
						description="Write the Unique IDs shown below on the remnants and return them to the indicated shelf."
					/>

					<List
						itemLayout="horizontal"
						dataSource={addMaterialLengthsResult?.data?.material_lengths}
						renderItem={(item, index) => (
							<List.Item>
								<List.Item.Meta
									title={
										<>
											<Title level={4} key={item.id}>
												{item.friendly_id}
											</Title>
											<Text>{item.material_shelf.display_name}</Text>
										</>
									}
								/>
							</List.Item>
						)}
					/>
				</div>
			) : (
				<>
					<Title level={5}>Return Remnant</Title>
					<Form
						form={form}
						layout="vertical"
						name="remnant_form"
						requiredMark={false}
						initialValues={{
							quantity: 1,
							override_width: false,
							override_height: false,
						}}
						size={useTabletMode ? 'large' : 'middle'}
					>
						<div className="flex flex-col">
							<div className="flex flex-row gap-2">
								<Form.Item
									label="Current Length"
									name={'current_length'}
									rules={[
										{
											required: true,
											message: 'Current length is required',
										},
										{
											type: 'number',
											max: 240,
											message: 'Must be a valid number',
										},
									]}
								>
									<InputNumber
										min={1}
										max={240}
										step={0.25}
										precision={2}
										controls={useTabletMode ? false : true}
										placeholder="Enter current length"
										style={{ width: '100%' }}
										suffix={'inches'}
									/>
								</Form.Item>

								<Form.Item
									label="Quantity"
									required
									style={{ marginBottom: 0 }}
								>
									<Input.Group compact>
										<Button onClick={decrement} icon={<MinusOutlined />} />
										<Form.Item
											name="quantity"
											noStyle
											rules={[
												{ required: true, message: 'Quantity is required' },
											]}
										>
											<InputNumber
												min={1}
												max={99}
												step={1}
												controls={false}
												placeholder="Enter quantity"
												style={{ textAlign: 'center' }}
											/>
										</Form.Item>
										<Button onClick={increment} icon={<PlusOutlined />} />
									</Input.Group>
								</Form.Item>
							</div>

							<div className="mb-5">
								<Checkbox
									checked={chooseLocationAutomatically}
								>
									Choose Location Automatically
								</Checkbox>
							</div>

							{!chooseLocationAutomatically ? (
								<Form.Item
									label="Location"
									name={'material_shelf'}
									rules={[{ required: true, message: 'Location is required' }]}
								>
									<Select
										showSearch={true}
										popupMatchSelectWidth={true}
										optionFilterProp="label"
										allowClear={true}
										options={materialShelvesQuery?.data?.map((shelf) => {
											return {
												value: shelf.id,
												label: shelf.display_name,
											};
										})}
										style={{ width: '300px' }}
									/>
								</Form.Item>
							) : null}

						<div className="flex flex-row py-2 gap-1">
							{materialShapeName === RECTANGLE_TUBE ||
							materialShapeName === ANGLE ||
							materialShapeName === CHANNEL ||
							materialShapeName === RECTANGLE_BAR ||
							materialShapeName === PLATE ? (
									<div className="flex flex-col gap-1">
										<Form.Item
											name={'override_width'}
											valuePropName="checked"
											noStyle
											shouldUpdate
										>
											<Checkbox
												value={overrideWidth}
												onClick={() => {
													form.setFieldValue('current_width', null);
													setOverrideWidth(!overrideWidth);
												}}
											>
												<Text strong>Width Changed</Text>
											</Checkbox>
										</Form.Item>

										{overrideWidth ? (
											<Form.Item
												name={'current_width'}
												rules={[
													{
														validator: (_, value) => {
															if (value == material?.data?.width) {
																return Promise.reject(
																	new Error(
																		`Should not be the same as the original`,
																	),
																);
															}
															if (value > material?.data?.width) {
																return Promise.reject(
																	new Error(`Cannot be greater than original`),
																);
															}
															return Promise.resolve();
														},
													},
													{
														required: true,
														message: 'Required',
													},
												]}
												shouldUpdate
											>
												<InputNumber
													min={0.25}
													step={0.25}
													style={{ width: '200px' }}
													suffix="in"
													placeholder="New Width"
												/>
											</Form.Item>
										) : null}
									</div>
									) : null}

							{materialShapeName === HEX_BAR ||
							materialShapeName === RECTANGLE_TUBE ||
							materialShapeName === ANGLE ||
							materialShapeName === CHANNEL ||
							materialShapeName === RECTANGLE_BAR ||
							materialShapeName === PLATE ? (
										<div className="flex flex-col gap-1">
											<Form.Item
												name={'override_height'}
												style={{ marginBottom: 0 }}
												valuePropName="checked"
												noStyle
											>
												<Checkbox
													value={overrideHeight}
													onClick={() => {
														form.setFieldValue('current_height', null);
														setOverrideHeight(!overrideHeight);
													}}
												>
													<Text strong>Height Changed</Text>
												</Checkbox>
											</Form.Item>

											{overrideHeight ? (
												<Form.Item
													name={'current_height'}
													rules={[
														{
															validator: (_, value) => {
																if (value == material?.data?.height) {
																	return Promise.reject(
																		new Error(
																			`Should not be the same as the original`,
																		),
																	);
																}
																if (value > material?.data?.height) {
																	return Promise.reject(
																		new Error(`Cannot be greater than original`),
																	);
																}
																return Promise.resolve();
															},
														},
														{
															required: true,
															message: 'Required',
														},
													]}
													shouldUpdate
												>
													<InputNumber
														min={0.25}
														step={0.25}
														style={{ width: '200px' }}
														suffix="in"
														placeholder="New Height"
													/>
												</Form.Item>
											) : null}
										</div>
								) : null}

							{materialShapeName === ROUND_BAR ||
								materialShapeName === ROUND_TUBE ? (
									<div className="flex flex-col gap-1">
										<Form.Item
											name={'override_outer_diameter'}
											valuePropName="checked"
											noStyle
											shouldUpdate
										>
											<Checkbox
												value={overrideOuterDiameter}
												onClick={() => {
													form.setFieldValue('current_outer_diameter', null);
													setOverrideOuterDiameter(!overrideOuterDiameter);
												}}
											>
												<Text strong>Outer Diameter Changed</Text>
											</Checkbox>
										</Form.Item>

										{overrideOuterDiameter ? (
											<Form.Item
												name={'current_outer_diameter'}
												rules={[
													{
														validator: (_, value) => {
															if (value == material?.data?.outer_diameter) {
																return Promise.reject(
																	new Error(
																		`Should not be the same as the original`,
																	),
																);
															}
															if (value > material?.data?.outer_diameter) {
																return Promise.reject(
																	new Error(`Cannot be greater than original`),
																);
															}
															return Promise.resolve();
														},
													},
													{
														required: true,
														message: 'Required',
													},
												]}
												shouldUpdate
											>
												<InputNumber
													min={0.25}
													step={0.25}
													style={{ width: '200px' }}
													suffix="in"
													placeholder="New Outer Diameter"
												/>
											</Form.Item>
										) : null}
									</div>
								) : null}

								{materialShapeName === ROUND_TUBE ? (
									<div className="flex flex-col gap-1">
										<Form.Item
											name={'override_inner_diameter'}
											valuePropName="checked"
											noStyle
											shouldUpdate
										>
											<Checkbox
												value={overrideInnerDiameter}
												onClick={() => {
													form.setFieldValue('current_inner_diameter', null);
													setOverrideInnerDiameter(!overrideInnerDiameter);
												}}
											>
												<Text strong>Inner Diameter Changed</Text>
											</Checkbox>
										</Form.Item>

										{overrideInnerDiameter ? (
											<Form.Item
												name={'current_inner_diameter'}
												rules={[
													{
														validator: (_, value) => {
															if (value == material?.data?.inner_diameter) {
																return Promise.reject(
																	new Error(
																		`Should not be the same as the original`,
																	),
																);
															}
															if (value > material?.data?.inner_diameter) {
																return Promise.reject(
																	new Error(`Cannot be greater than original`),
																);
															}
															return Promise.resolve();
														},
													},
													{
														required: true,
														message: 'Required',
													},
												]}
												shouldUpdate
											>
												<InputNumber
													min={0.25}
													step={0.25}
													style={{ width: '200px' }}
													suffix="in"
													placeholder="New Inner Diameter"
												/>
											</Form.Item>
										) : null}
									</div>
								) : null}

							{materialShapeName === RECTANGLE_TUBE ||
								materialShapeName === ANGLE ||
								materialShapeName === CHANNEL ? (
									<div className="flex flex-col gap-1">
										<Form.Item
											name={'wall_thickness'}
											valuePropName="checked"
											noStyle
											shouldUpdate
										>
											<Checkbox
												value={wallThickness}
												onClick={() => {
													form.setFieldValue('current_wall_thickness', null);
													setWallThickness(!wallThickness);
												}}
											>
												<Text strong>Wall Thickness Changed</Text>
											</Checkbox>
										</Form.Item>

										{wallThickness ? (
											<Form.Item
												name={'current_wall_thickness'}
												rules={[
													{
														validator: (_, value) => {
															if (value == material?.data?.wall_thickness) {
																return Promise.reject(
																	new Error(
																		`Should not be the same as the original`,
																	),
																);
															}
															if (value > material?.data?.wall_thickness) {
																return Promise.reject(
																	new Error(`Cannot be greater than original`),
																);
															}
															return Promise.resolve();
														},
													},
													{
														required: true,
														message: 'Required',
													},
												]}
												shouldUpdate
											>
												<InputNumber
													min={0.25}
													step={0.25}
													style={{ width: '200px' }}
													suffix="in"
													placeholder="New Wall Thickness"
												/>
											</Form.Item>
										) : null}
									</div>
								) : null}

							</div>

								{overrideHeight ||
									overrideWidth	||
									overrideOuterDiameter	||
									overrideInnerDiameter	||
									wallThickness ? (
									<Alert
										showIcon
										description="A new material will be created with the updated width and/or height. Links to the original material certificates will be maintained."
									/>
								) : null}
							</div>
					</Form>
				</>
			)}
		</Modal>
	);
};

export default ReturnRemnantsModal;
